/**
 * User data persistence via Supabase `user_data` table.
 * Each user gets key-value pairs stored as JSONB.
 * Falls back to localStorage when not authenticated.
 */
import { supabase } from "../../supabaseClient";

// In-memory cache to avoid repeated fetches within a session
const cache = {};

// In-flight prefetch promise. When the dashboard primes the cache via
// prefetchUserData(), individual loadUserData() calls firing in parallel
// from useEffects must wait for the batch to land — otherwise each one
// races ahead with its own per-key query and the batching does nothing.
let prefetchPromise = null;

async function getCurrentUserId() {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
}

/**
 * Load a value for the current user.
 * @param {string} key - The data key (e.g., "cert_progress", "saved_plans")
 * @param {*} fallback - Default value if not found
 * @param {object} [opts]
 * @param {boolean} [opts.forceRefresh] - Bypass in-memory cache and re-fetch
 *   from Supabase. Used by surfaces like the Portal that need to reflect
 *   cross-device writes (e.g. coach saves a plan on desktop, opens Portal
 *   on phone, expects to see the new plan).
 * @returns {Promise<*>} The stored value or fallback
 */
export async function loadUserData(key, fallback = {}, opts = {}) {
  // forceRefresh: drop any cached value so we go all the way back to Supabase.
  if (opts.forceRefresh && cache[key] !== undefined) {
    delete cache[key];
  }
  // Return cached if available. MISSING sentinel = prefetched but no
  // row — return the caller's fallback without re-querying.
  if (cache[key] !== undefined) {
    return cache[key] === MISSING ? fallback : cache[key];
  }

  // If a batch prefetch is in-flight, wait for it before falling through
  // to a per-key query. The batch may already cover this key.
  if (prefetchPromise) {
    try { await prefetchPromise; } catch {}
    if (cache[key] !== undefined) {
      return cache[key] === MISSING ? fallback : cache[key];
    }
  }

  const userId = await getCurrentUserId();
  if (!userId) {
    // Fallback to localStorage for unauthenticated users
    try { return JSON.parse(localStorage.getItem(`bam_${key}`) || JSON.stringify(fallback)); } catch { return fallback; }
  }

  // Use array select (not maybeSingle) so missing rows return 200 with [] at
  // the HTTP level instead of 404. maybeSingle sets the pgrst.object Accept
  // header which makes Supabase's REST layer emit 404 for empty results.
  const { data: rows, error } = await supabase
    .from("user_data")
    .select("value")
    .eq("user_id", userId)
    .eq("key", key)
    .limit(1);
  const data = rows?.[0];

  if (error || !data) {
    // Try migrating from localStorage on first load
    try {
      const lsKey = LS_KEY_MAP[key];
      if (lsKey) {
        const lsVal = JSON.parse(localStorage.getItem(lsKey) || "null");
        if (lsVal !== null) {
          // Migrate to Supabase
          await saveUserData(key, lsVal);
          cache[key] = lsVal;
          return lsVal;
        }
      }
    } catch {}
    cache[key] = fallback;
    return fallback;
  }

  cache[key] = data.value;
  return data.value;
}

/**
 * Save a value for the current user.
 * @param {string} key - The data key
 * @param {*} value - The value to store (will be JSON-serialized)
 * @throws when the write doesn't land, after rolling the cache back
 *
 * This used to swallow the upsert error entirely. The cache was updated
 * optimistically, so the UI showed a save that never happened and the next
 * reload read the empty DB and the work was gone — which is exactly what
 * "my training plans disappear when I refresh" was.
 *
 * Now: roll the cache back and throw, so a caller that cares can tell the
 * coach, and a caller that doesn't opts out explicitly with .catch(). Silence
 * is now a decision at the call site rather than a property of this function.
 */
export async function saveUserData(key, value) {
  const previous = cache[key];
  cache[key] = value;

  const userId = await getCurrentUserId();
  if (!userId) {
    // Fallback to localStorage
    try { localStorage.setItem(`bam_${key}`, JSON.stringify(value)); } catch {}
    return;
  }

  // Atomic upsert. Relies on the (user_id, key) UNIQUE constraint
  // added in 2026-05-08_user_data_unique_constraint.sql. Race-free and
  // a single round trip; the previous select-then-update-or-insert
  // pattern could produce duplicate rows under concurrent writes for
  // the same key.
  const { error } = await supabase.from("user_data").upsert(
    { user_id: userId, key, value, updated_at: new Date().toISOString() },
    { onConflict: "user_id,key" }
  );

  if (error) {
    // Put the cache back so the next read reflects what's actually stored
    // rather than what we hoped to store.
    if (previous === undefined) delete cache[key];
    else cache[key] = previous;
    throw error;
  }
}

/**
 * Clear the in-memory cache (call on logout)
 */
export function clearUserDataCache() {
  Object.keys(cache).forEach(k => delete cache[k]);
}

/**
 * Prefetch many keys at once. The dashboard previously hit user_data 6×
 * on mount (cert_progress, video_notes, watched_ids, fav_drills, fav_lists,
 * fav_list_membership) — one round-trip per key, plus an auth.getUser per
 * call. With 100 concurrent users that's a connection-pool fire.
 *
 * Call this once at dashboard mount with every key the dashboard reads;
 * the existing in-memory cache makes downstream loadUserData calls
 * cache hits (zero queries). Anything not yet in `cache` after this
 * runs is treated as "not stored" and uses the caller's fallback.
 *
 * Doesn't replace loadUserData — keys read for the first time after
 * mount still go through the per-key path.
 */
export async function prefetchUserData(keys) {
  if (!Array.isArray(keys) || keys.length === 0) return;
  if (prefetchPromise) return prefetchPromise;
  prefetchPromise = (async () => {
    try {
      const userId = await getCurrentUserId();
      if (!userId) return;
      const { data: rows, error } = await supabase
        .from("user_data")
        .select("key, value")
        .eq("user_id", userId)
        .in("key", keys);
      if (error) return;
      for (const r of rows || []) cache[r.key] = r.value;
      // Keys we asked for but didn't get a row for — mark them as
      // "missing" using a non-undefined sentinel so loadUserData hits
      // the cache and short-circuits to the caller's fallback. The
      // sentinel is a sealed object so equality checks against any
      // user-supplied value naturally fail.
      for (const k of keys) {
        if (cache[k] === undefined) cache[k] = MISSING;
      }
    } finally {
      prefetchPromise = null;
    }
  })();
  return prefetchPromise;
}

// Sentinel for prefetched keys with no stored row. loadUserData
// detects it and returns the caller's fallback without re-querying.
const MISSING = Object.freeze({ __userDataMissing: true });

// Mapping from our keys to old localStorage keys (for migration)
const LS_KEY_MAP = {
  cert_progress: "bam_cert_progress",
  cert_purchased: "bam_cert_purchased",
  saved_plans: "bam_saved_plans",
  plan_draft: "bam_plan_draft",
  video_notes: "bam_video_notes",
  watched_ids: "bam_watched_ids",
  ai_summaries: "bam_ai_summaries",
  coach_videos: "bam_coach_videos",
  player_dev_plans: "bam_player_dev_plans",
};
